import { Flame } from 'lucide-react'
import { AiCreditsIndicator } from '@/components/ai/AiCreditsIndicator'
import { Card } from '@/components/ui/Card'
import type { Streak } from '@/api/dashboard'
import { useMinCopy } from '@/i18n/useUiLocale'

interface StreakWidgetProps {
  streak: Streak
  embedded?: boolean
}

export function StreakWidget({ streak, embedded = false }: StreakWidgetProps) {
  const { dashboard } = useMinCopy()
  const { currentStreak, activeToday } = streak

  const content = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {dashboard.streak}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
            activeToday ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-muted'
          }`}
        >
          <Flame
            className={`h-6 w-6 ${activeToday ? 'text-orange-500' : 'text-muted-foreground'}`}
          />
        </div>
        <div>
          <p className="text-3xl font-bold tabular-nums leading-none">{currentStreak}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {currentStreak === 1 ? dashboard.dayInARow : dashboard.daysInARow}
          </p>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        {activeToday ? (
          <span className="font-medium text-green-600 dark:text-green-400">
            {dashboard.studiedToday}
          </span>
        ) : currentStreak > 0 ? (
          dashboard.keepStreakAlive
        ) : (
          dashboard.startStreak
        )}
      </p>
      <AiCreditsIndicator inline className="mt-4" />
    </>
  )

  if (embedded) return content

  return <Card className="p-5">{content}</Card>
}
